import { useMemo, useRef, useState } from 'react';
import { useProjectContext } from '../contexts/ProjectContext';
import {
  useCreateDocumento,
  useDeleteDocumento,
  downloadDocumento,
} from '../hooks/useDocumenti';
import { formatDate } from '../services/cashFlowCalculations';
import type { Documento, Finanziamento } from '../types';

const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8 MB

const CATEGORIE = ['contratto', 'preventivo', 'fattura', 'planimetria', 'altro'];

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function stripDataUrl(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

export function Documenti() {
  const { currentProject } = useProjectContext();
  const createMutation = useCreateDocumento();
  const deleteMutation = useDeleteDocumento();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const documenti: Documento[] = useMemo(
    () => currentProject?.documenti ?? [],
    [currentProject]
  );
  const finanziamenti: Finanziamento[] = currentProject?.finanziamenti ?? [];

  const [file, setFile] = useState<File | null>(null);
  const [nome, setNome] = useState('');
  const [categoria, setCategoria] = useState('contratto');
  const [finanziamentoId, setFinanziamentoId] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrore(null);
    const f = e.target.files?.[0] || null;
    if (f && f.size > MAX_FILE_SIZE) {
      setErrore('File troppo grande (max 8 MB)');
      setFile(null);
      return;
    }
    setFile(f);
    if (f && !nome) {
      setNome(f.name.replace(/\.[^.]+$/, ''));
    }
  };

  const resetForm = () => {
    setFile(null);
    setNome('');
    setCategoria('contratto');
    setFinanziamentoId('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentProject || !file) return;
    setErrore(null);

    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Lettura file fallita'));
        reader.readAsDataURL(file);
      });

      await createMutation.mutateAsync({
        projectId: currentProject.id,
        finanziamentoId: finanziamentoId || undefined,
        nome: nome || file.name,
        categoria,
        file: {
          data: stripDataUrl(dataUrl),
          mediaType: file.type,
          filename: file.name,
        },
      });
      resetForm();
    } catch (err) {
      setErrore(
        err instanceof Error ? err.message : 'Caricamento non riuscito'
      );
    }
  };

  const handleDownload = async (doc: Documento) => {
    setDownloadingId(doc.id);
    try {
      await downloadDocumento(doc.id, doc.originalName);
    } catch {
      setErrore('Download non riuscito');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (doc: Documento) => {
    if (!currentProject) return;
    if (!window.confirm(`Eliminare il documento "${doc.nome}"?`)) return;
    try {
      await deleteMutation.mutateAsync({ id: doc.id, projectId: currentProject.id });
    } catch {
      setErrore('Eliminazione non riuscita');
    }
  };

  if (!currentProject) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Seleziona un progetto per gestire i documenti</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Documenti</h2>
        <p className="text-gray-600 mt-1">Contratti, preventivi e allegati del progetto</p>
      </div>

      {errore && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {errore}
        </div>
      )}

      {/* Upload form */}
      <form onSubmit={handleUpload} className="bg-white rounded-lg shadow p-6 space-y-4">
        <h3 className="font-semibold text-gray-900">Carica un documento</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="input-group">
            <label className="input-label" htmlFor="doc-file">File (PDF, immagine, doc — max 8 MB)</label>
            <input
              id="doc-file"
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
              className="w-full text-sm"
            />
          </div>

          <div className="input-group">
            <label className="input-label" htmlFor="doc-nome">Nome</label>
            <input
              id="doc-nome"
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="w-full"
              placeholder="es. Contratto prestito"
            />
          </div>

          <div className="input-group">
            <label className="input-label" htmlFor="doc-categoria">Categoria</label>
            <select
              id="doc-categoria"
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
              className="w-full"
            >
              {CATEGORIE.map((c) => (
                <option key={c} value={c}>
                  {c.charAt(0).toUpperCase() + c.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <label className="input-label" htmlFor="doc-fin">Associa a finanziamento (opzionale)</label>
            <select
              id="doc-fin"
              value={finanziamentoId}
              onChange={(e) => setFinanziamentoId(e.target.value)}
              className="w-full"
            >
              <option value="">— Nessuno —</option>
              {finanziamenti.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nome}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="btn-primary"
            disabled={!file || createMutation.isPending}
          >
            {createMutation.isPending ? 'Caricamento...' : 'Carica documento'}
          </button>
        </div>
      </form>

      {/* Lista documenti */}
      {documenti.length === 0 ? (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <div className="text-5xl mb-3">📄</div>
          <h3 className="text-lg font-medium text-gray-900">Nessun documento</h3>
          <p className="mt-2 text-sm text-gray-500">
            Carica il primo documento usando il modulo qui sopra.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden divide-y divide-gray-100">
          {documenti.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center text-lg shrink-0">
                  {doc.mimeType === 'application/pdf' ? '📕' : doc.mimeType.startsWith('image/') ? '🖼️' : '📄'}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{doc.nome}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {doc.categoria ? `${doc.categoria} · ` : ''}
                    {formatSize(doc.size)} · {formatDate(doc.createdAt)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 ml-4 shrink-0">
                <button
                  onClick={() => handleDownload(doc)}
                  disabled={downloadingId === doc.id}
                  className="px-3 py-1.5 text-sm text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Scarica"
                >
                  {downloadingId === doc.id ? '...' : 'Scarica'}
                </button>
                <button
                  onClick={() => handleDelete(doc)}
                  className="p-2 text-gray-400 hover:text-danger-600 hover:bg-gray-50 rounded-lg transition-colors"
                  title="Elimina"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
